'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

const libCustomerRecordManifests = require('../providers/CustomerRecordManifests-Merge.js');

/**
 * DocumentList — a net-new, filterable Documents (Reports + Forms) browse screen for the config app, built as the
 * document cognate of the Observation screen (one filter bar; List + Charts view switcher; embeddable + project-locked
 * in the Project workspace). Documents have NO Solr/search index (unlike observations) — the only document Solr code in
 * the API is dead/commented — so this is backed by Meadow: `GET /1.0/Documents/FilteredTo/<FBV~…~FBL~…>/<begin>/<cap>`
 * plus `/Documents/Count/FilteredTo/…` (the `Document` table is indexed on exactly the facet columns, so filtered
 * queries are fast; the risky case is an unscoped customer-wide scan). Reference impl: `web/document_search/`.
 *
 * A document's "reporting status" analog is its own approval `Status` (Draft → Submitted → Pending → Rejected →
 * Approved). `DocumentType` is a ReportNamedInstance hash (not a table) resolved to a display name where possible.
 *
 * Charts view computes its distributions with parallel `Count/FilteredTo` queries per facet value (status, type) —
 * the contract-safe fallback for "faceting" without a Solr index.
 *
 * NOT YET WIRED (needs a logged-in pass to validate the DocumentApproval state-machine contracts + test safely, since
 * transitions mutate real approval state): the approval ACTIONS (approve / reject / submit) and actionable-users. The
 * per-row/detail Status is shown read-only for now.
 */

// The document approval status vocabulary (from the API state machine: Draft → Submitted → Pending → Rejected → Approved).
const _STATUSES = [ 'Draft', 'Submitted', 'Pending', 'Rejected', 'Approved' ];
const _STATUS_COLOR = { Draft: '#8b98a3', Submitted: '#3a86a8', Pending: '#c98a2e', Rejected: '#bd6631', Approved: '#4f9d69' };
const _CAT = [ '#2e7d74', '#3a86a8', '#6a6fae', '#9a6fb0', '#b5734e', '#4f9d69', '#c2708f', '#7f8b3d', '#c98a2e', '#5a8bce' ];

class DocumentListProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._mount = '#DocumentList-Host';
		this._filter = { SearchText: '', Types: [], Statuses: [], States: [], Authors: [], AuthorNames: {}, From: '', To: '', IDProject: 0, ProjectName: '', Form: '' };
		this._layout = libCustomerRecordManifests.documentBrowseLayout(null, null);
		this._projectNames = {};
		this._stateDropdownOpen = false;
		this._page = 0;
		this._cap = 25;
		this._total = 0;
		this._rows = [];
		this._authorNames = {};
		this._typeOptions = null;      // [{ Hash, Label }] resolved from ReportNamedInstance where possible
		this._typeGrouped = false;     // true once type display names resolved (else retry next load)
		this._loading = false;
		this._error = '';
		this._loaded = false;

		this._viewMode = 'list';       // 'list' | 'charts'
		this._embeddedProjectID = 0;   // when >0, embedded in a Project workspace tab: locked to project, no Project filter
		this._fullscreen = false;      // standalone "full screen" — overlay the whole browser view (see toggleFullscreen)
		this._typeDropdownOpen = false;
		this._statusDropdownOpen = false;

		// charts state
		this._baseline = 0;
		this._chartStatus = {};        // status -> count under current (minus-status) filter
		this._chartTypes = [];         // [{ hash, label, count }] under current (minus-type) filter
		this._chartLatencyMs = 0;
		this._chartsLoaded = false;
		this._prevKPI = null;
		this._chartsCache = {};        // filter signature -> computed charts (instant re-view)
		this._loadGen = 0;             // bumped each charts load; in-flight type streaming checks it to cancel stale work
		this._typeProgress = { done: 0, total: 0 };
		this._lastTypePaint = 0;

		if (typeof document !== 'undefined')
		{
			this._onDocClick = (pEvent) =>
			{
				if (!this._typeDropdownOpen && !this._statusDropdownOpen && !this._stateDropdownOpen)
				{
					return;
				}
				try
				{
					const inType = document.querySelector('.dol-dd-type'); const inStatus = document.querySelector('.dol-dd-status');
					let changed = false;
					if (this._typeDropdownOpen && inType && !inType.contains(pEvent.target)) { this._typeDropdownOpen = false; changed = true; }
					if (this._statusDropdownOpen && inStatus && !inStatus.contains(pEvent.target)) { this._statusDropdownOpen = false; changed = true; }
					const tmpInState = document.querySelector('.dol-dd-state');
					if (this._stateDropdownOpen && tmpInState && !tmpInState.contains(pEvent.target))
					{
						this._stateDropdownOpen = false;
						changed = true;
					}
					if (changed) { this.render(); }
				}
				catch (pError) { /* */ }
			};
			document.addEventListener('click', this._onDocClick, true);
			// Esc leaves full screen (routed through toggleFullscreen).
			this._onKeydown = (pEvent) => { if (pEvent.key === 'Escape' && this._fullscreen) { this.toggleFullscreen(); } };
			document.addEventListener('keydown', this._onKeydown);
		}

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('DocumentList-CSS', _CSS, 520);
		}
	}

	get cfg() { return this.options || {}; }
	get providerName() { return this.cfg.ProviderIdentifier || 'DocumentList'; }
	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	get customerID() { return (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0; }

	_get(pURL) { return new Promise((resolve) => this.restClient.getJSON(pURL, (e, b) => resolve(e ? null : b))); }
	_esc(pV) { return String(pV == null ? '' : pV).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
	_mediaUrl(pPath) { const rc = this.restClient; if (rc && (typeof rc.formatUrl === 'function')) { try { return rc.formatUrl(pPath); } catch (e) { /* */ } } return `/1.0/${pPath}`; }
	_fmt(pN) { return Math.round(Number(pN) || 0).toLocaleString(); }
	_compact(pN) { const n = Number(pN) || 0; if (n >= 1e6) { return `${(n / 1e6).toFixed(1)}M`; } if (n >= 1e3) { return `${Math.round(n / 1e3)}K`; } return String(n); }
	_fmtDate(pV) { if (!pV) { return ''; } try { const d = new Date(pV); if (!Number.isNaN(d.getTime())) { return d.toLocaleDateString(); } } catch (e) { /* */ } return String(pV).slice(0, 10); }
	_escFilter(pV) { return String(pV == null ? '' : pV).replace(/~/g, ''); }

	// --- Meadow filter stanzas (dates use GE/LT per the Meadow convention — never GTE/LTE) ---
	_buildFilter(pExclude)
	{
		const f = this._filter; const parts = [ `FBV~IDCustomer~EQ~${this.customerID}`, 'FBV~Deleted~EQ~0' ];
		if (f.IDProject) { parts.push(`FBV~IDProject~EQ~${Number(f.IDProject)}`); }
		if (f.SearchText && f.SearchText.trim()) { parts.push(`FBV~Name~LK~${encodeURIComponent(`%${this._escFilter(f.SearchText.trim())}%`)}`); }
		if (f.Types && f.Types.length && pExclude !== 'type') { parts.push(`FBL~DocumentType~INN~${f.Types.map((t) => this._escFilter(t)).join(',')}`); }
		if (f.Statuses && f.Statuses.length && pExclude !== 'status')
		{
			parts.push(`FBL~Status~INN~${f.Statuses.map((s) => encodeURIComponent(this._escFilter(s))).join(',')}`);
		}
		if (f.States && f.States.length)
		{
			parts.push(`FBL~StateName~INN~${f.States.map((pState) => encodeURIComponent(this._escFilter(pState))).join(',')}`);
		}
		if (f.Authors && f.Authors.length) { parts.push(`FBL~CreatingIDUser~INN~${f.Authors.map(Number).join(',')}`); }
		if (f.From) { parts.push(`FBV~DocumentDate~GE~${new Date(f.From).toISOString()}`); }
		if (f.To) { parts.push(`FBV~DocumentDate~LT~${new Date(`${f.To}T23:59:59`).toISOString()}`); }
		if (f.Form === '0' || f.Form === '1') { parts.push(`FBV~Form~EQ~${f.Form}`); }
		return parts.join('~');
	}

	async loadAsync()
	{
		this._loading = true; this._error = ''; this._loaded = true;
		this.render();
		try
		{
			await Promise.all([ this._ensureTypeOptions(), this._loadLayoutAsync() ]);
			if (this._viewMode === 'charts') { await this._loadChartsAsync(); }
			else { await this._loadListAsync(); }
		}
		catch (pError) { this._error = (pError && pError.message) || String(pError); this._rows = []; this._total = 0; }
		this._loading = false;
		this.render();
	}

	async _loadListAsync()
	{
		const flt = this._buildFilter();
		const [ tmpRows, tmpCount ] = await Promise.all([
			this._get(`Documents/FilteredTo/${flt}~FSF~DocumentDate~DESC~0/${this._page * this._cap}/${this._cap}`),
			this._get(`Documents/Count/FilteredTo/${flt}`),
		]);
		if (!tmpRows) { throw new Error('The document search did not respond (are you signed in?).'); }
		this._rows = Array.isArray(tmpRows) ? tmpRows : [];
		this._total = (tmpCount && typeof tmpCount.Count === 'number') ? tmpCount.Count : this._rows.length;
		await Promise.all([ this._resolveAuthors(), this._resolveProjects() ]);
	}

	/**
	 * The signed-in customer's Document List / Filter manifests decide the columns and the Status / State pick lists.
	 * @return {Promise<void>}
	 */
	async _loadLayoutAsync()
	{
		const tmpManifests = this.pict.providers.CustomerRecordManifests;
		if (!tmpManifests)
		{
			return;
		}
		try
		{
			this._layout = await tmpManifests.getDocumentBrowseLayoutAsync();
		}
		catch (pError)
		{
			this.pict.log.warn(`DocumentList: customer document manifests not applied: ${(pError && pError.message) || pError}`);
		}
	}

	/** @return {Array<{ Key: string, Label: string }>} the columns to draw */
	_columns()
	{
		return this._layout.Columns.filter((pColumn) => !(this._embeddedProjectID && (pColumn.Key === 'IDProject')));
	}

	/**
	 * Project names for the Project column, when it shows.
	 * @return {Promise<void>}
	 */
	async _resolveProjects()
	{
		if (!this._columns().some((pColumn) => pColumn.Key === 'IDProject'))
		{
			return;
		}
		const tmpIDs = [ ...new Set(this._rows.map((pDoc) => Number(pDoc.IDProject)).filter((pID) => pID && !this._projectNames[pID])) ];
		if (!tmpIDs.length)
		{
			return;
		}
		const tmpProjects = await this._get(`Projects/Lite/FilteredTo/FBL~IDProject~INN~${tmpIDs.join(',')}/0/${tmpIDs.length}`);
		(Array.isArray(tmpProjects) ? tmpProjects : []).forEach((pProject) =>
		{
			this._projectNames[pProject.IDProject] = pProject.Value || pProject.Name || `#${pProject.IDProject}`;
		});
	}

	// Charts — no document index, so counts are per-value Meadow Count/FilteredTo (DocumentType/Status are indexed, so
	// each individual count is fast; it's the QUANTITY of round-trips that hurts). To stay responsive we: (1) load KPIs +
	// the approval-status funnel FIRST (total + one count per status ~1s), (2) STREAM the type breakdown in progressively
	// (bounded concurrency, patched into the panel as counts land), and (3) cache the whole result per filter signature so
	// re-viewing is instant. A newer load bumps _loadGen, which cancels any in-flight streaming.
	_countFor(pFilter) { return this._get(`Documents/Count/FilteredTo/${pFilter}`).then((r) => (r && typeof r.Count === 'number') ? r.Count : 0); }
	_filterSig() { return JSON.stringify(this._filter); }

	async _loadChartsAsync()
	{
		const tmpGen = ++this._loadGen;
		const tmpSig = this._filterSig();

		const tmpCached = this._chartsCache[tmpSig];
		if (tmpCached)
		{
			this._total = tmpCached.total; this._chartStatus = tmpCached.status; this._chartTypes = tmpCached.types;
			this._chartLatencyMs = tmpCached.ms; this._typeProgress = { done: tmpCached.types.length, total: tmpCached.types.length };
			this._chartsLoaded = true;
			return;
		}

		// Phase A — KPIs + approval-status funnel (fast).
		const tmpT0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
		const tmpStatusBase = this._buildFilter('status');
		const [ tmpTotal, tmpStatusCounts ] = await Promise.all([
			this._countFor(this._buildFilter()),
			Promise.all(_STATUSES.map((s) => this._countFor(`${tmpStatusBase}~FBV~Status~EQ~${this._escFilter(s)}`))),
		]);
		if (tmpGen !== this._loadGen) { return; }   // superseded by a newer load
		this._total = tmpTotal;
		this._chartStatus = {}; _STATUSES.forEach((s, i) => { this._chartStatus[s] = tmpStatusCounts[i] || 0; });
		this._chartLatencyMs = Math.max(1, Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - tmpT0));
		this._chartTypes = [];
		this._typeProgress = { done: 0, total: (this._typeOptions || []).length };
		this._chartsLoaded = true;

		// Phase B — stream the type breakdown (NOT awaited; loadAsync renders Phase A immediately, then this patches in).
		this._streamTypeBreakdown(tmpGen, tmpSig);
	}

	async _streamTypeBreakdown(pGen, pSig)
	{
		const tmpTypeBase = this._buildFilter('type');
		let tmpOpts = null;
		// Bounded scope (a project/date/etc. narrowed the set) → a distinct read returns ONLY the types actually present,
		// so we count a handful instead of every configured type. Distinct scans the result set, so we only try it when
		// the set is small enough (a customer-wide distinct over ~800k rows times out), and race it with a timeout.
		if (this._total > 0 && this._total <= 100000)
		{
			const tmpDistinct = await Promise.race([
				this._get(`Documents/Distinct/DocumentType/FilteredTo/${tmpTypeBase}`),
				new Promise((pResolve) => setTimeout(() => pResolve('__timeout__'), 5000)),
			]);
			if (pGen !== this._loadGen) { return; }
			if (Array.isArray(tmpDistinct))
			{
				const tmpPresent = tmpDistinct.map((r) => (r && (r.DocumentType != null)) ? r.DocumentType : r).filter((h) => (h != null && h !== ''));
				tmpOpts = tmpPresent.map((h) => { const o = (this._typeOptions || []).find((x) => x.Hash === h); return { Hash: h, Label: o ? o.Label : String(h) }; });
			}
		}
		if (!tmpOpts) { tmpOpts = (this._typeOptions || []).slice(); }
		this._typeProgress = { done: 0, total: tmpOpts.length };
		this._patchTypePanel(pGen, false);
		const tmpResults = [];
		let tmpIdx = 0;
		const fWorker = async () =>
		{
			while (tmpIdx < tmpOpts.length)
			{
				const o = tmpOpts[tmpIdx++];
				const c = await this._countFor(`${tmpTypeBase}~FBV~DocumentType~EQ~${this._escFilter(o.Hash)}`);
				if (pGen !== this._loadGen) { return; }   // a newer load superseded this stream
				tmpResults.push({ hash: o.Hash, label: o.Label, count: c });
				this._chartTypes = tmpResults.slice().sort((a, b) => (b.count - a.count));
				this._typeProgress = { done: tmpResults.length, total: tmpOpts.length };
				this._patchTypePanel(pGen, false);
			}
		};
		await Promise.all(Array.from({ length: Math.min(6, tmpOpts.length || 1) }, fWorker));
		if (pGen !== this._loadGen) { return; }
		this._typeProgress = { done: tmpOpts.length, total: tmpOpts.length };
		this._chartsCache[pSig] = { total: this._total, status: this._chartStatus, types: this._chartTypes, ms: this._chartLatencyMs };
		this._patchTypePanel(pGen, true);
	}

	// Patch ONLY the type panel + the "types" KPI in place (throttled) so streaming counts don't re-render / re-tween the rest.
	_patchTypePanel(pGen, pFinal)
	{
		if (pGen !== this._loadGen || this._viewMode !== 'charts') { return; }
		const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
		if (!pFinal && (now - (this._lastTypePaint || 0)) < 280) { return; }
		this._lastTypePaint = now;
		const tmpCA = this.pict.ContentAssignment; if (!tmpCA) { return; }
		const tmpPanel = tmpCA.getElement(`${this._mount} #dol-typepanel`);
		if (tmpPanel && tmpPanel.length) { tmpPanel[0].innerHTML = this._typeChartInnerHTML(); }
		const tmpKpi = tmpCA.getElement(`${this._mount} .dxp-v[data-kpi="types"]`);
		if (tmpKpi && tmpKpi.length) { tmpKpi[0].textContent = this._fmt(this._chartTypes.filter((t) => t.count > 0).length); }
	}

	// Resolve DocumentType hashes → display names via ReportNamedInstance (per-project when scoped, else customer-wide).
	async _ensureTypeOptions()
	{
		if (this._typeOptions && this._typeGrouped) { return; }
		let tmpRows = null;
		if (this._filter.IDProject) { tmpRows = await this._get(`ReportNamedInstancesByProject/${Number(this._filter.IDProject)}/0/500`); }
		if (!Array.isArray(tmpRows) || !tmpRows.length) { tmpRows = await this._get(`ReportNamedInstances/FilteredTo/FBV~IDCustomer~EQ~${this.customerID}~FBV~Deleted~EQ~0/0/500`); }
		const tmpByHash = {};
		(Array.isArray(tmpRows) ? tmpRows : []).forEach((r) =>
		{
			const tmpHash = r.Hash || r.DocumentType || r.ReportTypeName; if (!tmpHash) { return; }
			const tmpLabel = r.Name || r.DisplayName || r.Title || tmpHash;
			if (!tmpByHash[tmpHash]) { tmpByHash[tmpHash] = { Hash: tmpHash, Label: String(tmpLabel).trim() }; }
		});
		const tmpOpts = Object.keys(tmpByHash).map((k) => tmpByHash[k]).sort((a, b) => a.Label.localeCompare(b.Label));
		this._typeOptions = tmpOpts;
		this._typeGrouped = tmpOpts.length > 0;   // retry next load if it didn't resolve
	}

	async _resolveAuthors()
	{
		const tmpIDs = [ ...new Set(this._rows.map((d) => d.CreatingIDUser).filter((x) => x && !this._authorNames[x])) ];
		if (!tmpIDs.length) { return; }
		const tmpUsers = await this._get(`Users/FilteredTo/FBL~IDUser~INN~${tmpIDs.join(',')}/0/${tmpIDs.length}`);
		(Array.isArray(tmpUsers) ? tmpUsers : []).forEach((u) =>
		{
			// filter(Boolean) keeps a literal "-" placeholder some imported users carry in an empty name part, leaving a
			// dangling "Ciolino, David -". Trim each part and drop punctuation-only placeholders before joining.
			const tmpParts = [ u.NameFirst, u.NameLast ]
				.map((pPart) => String(pPart == null ? '' : pPart).trim())
				.filter((pPart) => pPart && !/^[-–—]+$/.test(pPart));
			this._authorNames[u.IDUser] = tmpParts.join(' ') || u.LoginID || `#${u.IDUser}`;
		});
	}

	_typeLabel(pHash) { const o = (this._typeOptions || []).find((t) => t.Hash === pHash); return o ? o.Label : (pHash || ''); }

	// --- interactions ---
	setSearch(pV) { this._filter.SearchText = String(pV == null ? '' : pV); }
	setDateFrom(pV) { this._filter.From = pV || ''; }
	setDateTo(pV) { this._filter.To = pV || ''; }
	setForm(pV) { this._filter.Form = pV || ''; this.applyFilters(); }
	toggleTypeDropdown() { this._typeDropdownOpen = !this._typeDropdownOpen; this._statusDropdownOpen = false; this.render(); }
	toggleStatusDropdown() { this._statusDropdownOpen = !this._statusDropdownOpen; this._typeDropdownOpen = false; this.render(); }
	/** Open or close the State pick list. */
	toggleStateDropdown()
	{
		this._stateDropdownOpen = !this._stateDropdownOpen;
		this._typeDropdownOpen = false;
		this._statusDropdownOpen = false;
		this.render();
	}
	/**
	 * Add or remove a state name from the filter.
	 * @param {string} pState
	 */
	toggleState(pState)
	{
		const tmpState = String(pState);
		const tmpIndex = this._filter.States.indexOf(tmpState);
		if (tmpIndex >= 0)
		{
			this._filter.States.splice(tmpIndex, 1);
		}
		else
		{
			this._filter.States.push(tmpState);
		}
		this.applyFilters();
	}
	toggleType(pHash)
	{
		const h = String(pHash); const i = this._filter.Types.indexOf(h);
		if (i >= 0) { this._filter.Types.splice(i, 1); } else { this._filter.Types.push(h); }
		this.applyFilters();
	}
	clearTypes() { if (!this._filter.Types.length) { return; } this._filter.Types = []; this.applyFilters(); }
	toggleStatus(pStatus)
	{
		const s = String(pStatus); const i = this._filter.Statuses.indexOf(s);
		if (i >= 0) { this._filter.Statuses.splice(i, 1); } else { this._filter.Statuses.push(s); }
		this.applyFilters();
	}
	setStatusOnly(pStatus) { this._filter.Statuses = pStatus ? [ String(pStatus) ] : []; this.applyFilters(); }
	removeAuthor(pID) { const id = Number(pID); this._filter.Authors = this._filter.Authors.filter((x) => Number(x) !== id); this.applyFilters(); }
	addAuthor(pID, pName) { const id = Number(pID); if (!id) { return; } if (this._filter.Authors.map(Number).indexOf(id) < 0) { this._filter.Authors.push(id); if (pName) { this._authorNames[id] = pName; } } this.applyFilters(); }
	selectProject(pID, pName)
	{
		this._filter.IDProject = Number(pID) || 0; this._filter.ProjectName = pName || '';
		this._typeOptions = null; this._typeGrouped = false;   // types are project-scoped — re-resolve
		this.applyFilters();
	}
	clearFilters()
	{
		const tmpProj = this._embeddedProjectID || 0;
		this._filter = { SearchText: '', Types: [], Statuses: [], States: [], Authors: [], AuthorNames: {}, From: '', To: '', IDProject: tmpProj, ProjectName: this._filter.ProjectName || '', Form: '' };
		if (!tmpProj) { this._typeOptions = null; this._typeGrouped = false; }
		this.applyFilters();
	}
	applyFilters() { this._page = 0; this.loadAsync(); }
	setPage(pPage) { const tmpMax = Math.max(0, Math.ceil(this._total / this._cap) - 1); this._page = Math.min(Math.max(0, pPage), tmpMax); this.loadAsync(); }
	setViewMode(pMode) { if ((pMode !== 'list' && pMode !== 'charts') || this._viewMode === pMode) { return; } this._viewMode = pMode; this.loadAsync(); }
	// Full screen: overlay the dashboard across the whole browser view (above the app nav). State is a provider field so it
	// survives the shell rebuild; `.dol-fs` composites on its own layer (CSS transform) so it paints immediately. Navigating
	// away replaces the host, tearing the fixed overlay down with it (no leak). Esc exits.
	toggleFullscreen() { if (this._embeddedProjectID) { return; } this._fullscreen = !this._fullscreen; this.render(); }
	get _docRender() { return this.pict.HeadlightDocumentRender; }
	// Open the document as same-origin HTML via the render service (Report/{GUID}/index.html) — NEVER Fieldbook.
	// pReadOnly=false loads the editable form viewer/editor (editable when the doc isn't Locked); pReadOnly=true loads
	// the read-only HTML render. Falls back to the PDF only if the service is somehow unavailable.
	openHTML(pID, pReadOnly)
	{
		const id = Number(pID) || 0; if (!id) { return; }
		const svc = this._docRender;
		if (!svc || (typeof svc.openDocument !== 'function'))
		{
			try { window.open(this._mediaUrl(`Document/${id}/DCR.pdf`), '_blank', 'noopener'); } catch (e) { /* */ }
			return;
		}
		svc.openDocument(id, { target: '_blank', variation: pReadOnly ? 'html' : 'default' }, (pError) =>
		{
			if (pError) { try { this.pict.log.error(`Open document ${id} failed: ${pError.message || pError}`); } catch (e) { /* */ } }
		});
	}
	downloadPDF(pID)
	{
		const id = Number(pID) || 0; if (!id) { return; }
		const svc = this._docRender;
		if (svc && (typeof svc.downloadPDFLink === 'function')) { svc.downloadPDFLink(id); return; }
		try { window.open(this._mediaUrl(`Document/${id}/DCR.pdf`), '_blank', 'noopener'); } catch (e) { /* */ }
	}

	/**
	 * Fails closed: the Delete button stays hidden until the capability map is ready. A document grant can allow
	 * delete without the role permission, and the API only reports `CanDelete` on a single-document read, so the
	 * button shows on every unlocked row and `documentAllowsDelete` decides on click.
	 * @return {boolean}
	 */
	canDeleteDocument()
	{
		const tmpPermissions = this.pict.fable && this.pict.fable.EffectivePermissionsService;
		return !!(tmpPermissions && tmpPermissions.currentUserReady && tmpPermissions.currentUserReady());
	}

	/**
	 * Whether this user may delete this document: the role permission, or the document's own `CanDelete`.
	 * @param {number} pID - IDDocument
	 * @return {Promise<boolean>}
	 */
	async documentAllowsDelete(pID)
	{
		if (this.pict.fable.EffectivePermissionsService.canHash('Document-Delete'))
		{
			return true;
		}
		const tmpRecord = await this._get(`Document/${pID}`);
		return !!(tmpRecord && tmpRecord.CanDelete);
	}

	/**
	 * Delete a document from the list, after the user confirms. Locked documents can't be deleted here.
	 * @param {number} pID - IDDocument
	 * @return {Promise<void>}
	 */
	async deleteDocument(pID)
	{
		const tmpID = Number(pID) || 0;
		const tmpDoc = (this._rows || []).find((pRow) => Number(pRow.IDDocument) === tmpID);
		if (!tmpID || !this.canDeleteDocument() || (tmpDoc && tmpDoc.Locked))
		{
			return;
		}
		if (!(await this.documentAllowsDelete(tmpID)))
		{
			const tmpRefusalPanels = this.pict.views.HeadlightPanels;
			if (tmpRefusalPanels && (typeof tmpRefusalPanels.toast === 'function'))
			{
				tmpRefusalPanels.toast({ type: 'is-warning', message: 'You do not have permission to delete this document.' });
			}
			return;
		}
		const tmpLabel = (tmpDoc && tmpDoc.Name) ? `"${tmpDoc.Name}"` : 'this document';
		if ((typeof window !== 'undefined') && (typeof window.confirm === 'function') && !window.confirm(`Delete ${tmpLabel}?`))
		{
			return;
		}
		const tmpError = await new Promise((resolve) => this.restClient.deleteEntity('Document', tmpID, (pError) => resolve(pError)));
		const tmpPanels = this.pict.views.HeadlightPanels;
		if (tmpPanels && (typeof tmpPanels.toast === 'function'))
		{
			tmpPanels.toast(tmpError ? { type: 'is-danger', message: `Could not delete the document: ${(tmpError && tmpError.message) || tmpError}` } : { type: 'is-success', message: 'Document deleted.' });
		}
		await this.loadAsync();
	}

	// --- embed API (Project workspace Forms tab): lock to project, hide the Project filter, paint into the tab host ---
	openInto(pIDProject, pHostSelector)
	{
		const tmpID = Number(pIDProject) || 0;
		const tmpHost = pHostSelector || this._mount;
		const tmpChanged = (this._embeddedProjectID !== tmpID) || (this._mount !== tmpHost);
		this._embeddedProjectID = tmpID;
		this._mount = tmpHost;
		if (this._filter.IDProject !== tmpID) { this._filter.IDProject = tmpID; this._filter.ProjectName = ''; this._typeOptions = null; this._typeGrouped = false; }
		if (tmpChanged || !this._loaded)
		{
			try { if (this.pict.CSSMap && (typeof this.pict.CSSMap.injectCSS === 'function')) { this.pict.CSSMap.injectCSS(); } } catch (pError) { /* */ }
			this._page = 0; this.loadAsync();
		}
		else { this.render(); }
	}
	exitEmbed()
	{
		if (!this._embeddedProjectID && this._mount === '#DocumentList-Host') { return; }
		this._embeddedProjectID = 0; this._mount = '#DocumentList-Host';
		this._filter.IDProject = 0; this._filter.ProjectName = ''; this._typeOptions = null; this._typeGrouped = false;
	}

	// --- render ---
	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement(this._mount).length === 0) { return; }
		tmpCA.assignContent(this._mount, this._shellHTML());
		this._mountPickers();
		if (this._viewMode === 'charts') { this._tweenChartKPIs(); }
	}

	_shellHTML()
	{
		return /*html*/`
			<div class="dol${this._embeddedProjectID ? ' dol-embedded' : ''}${this._fullscreen ? ' dol-fs' : ''}">
				${this._embeddedProjectID ? '' : `<div class="dol-head"><div class="dol-title"><i class="fas fa-file-lines"></i> Documents</div></div>`}
				${this._filterBarHTML()}
				${this._viewTabsHTML()}
				<div id="dol-results">${this._bodyHTML()}</div>
			</div>`;
	}

	_viewTabsHTML()
	{
		const P = this.providerName;
		const tab = (pMode, pIcon, pLabel) => `<button class="dol-vtab${this._viewMode === pMode ? ' dol-vtab-on' : ''}" onclick="_Pict.providers.${P}.setViewMode('${pMode}')"><i class="fas ${pIcon}"></i> ${pLabel}</button>`;
		const tmpCount = this._loaded ? `<span class="dol-vtabcount">${this._total.toLocaleString()} document${this._total === 1 ? '' : 's'}</span>` : '';
		// Full-screen toggle — standalone only (in an embedded Project tab there's no app chrome to escape).
		const tmpFsBtn = this._embeddedProjectID ? '' : `<button class="dol-fsbtn${this._fullscreen ? ' dol-fsbtn-on' : ''}" onclick="_Pict.providers.${P}.toggleFullscreen()" title="${this._fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}"><i class="fas ${this._fullscreen ? 'fa-compress' : 'fa-expand'}"></i> ${this._fullscreen ? 'Exit full screen' : 'Full screen'}</button>`;
		return `<div class="dol-viewtabs-row"><div class="dol-viewtabs">${tab('list', 'fa-list-ul', 'List')}${tab('charts', 'fa-chart-simple', 'Charts')}</div><div class="dol-vtabs-right">${tmpCount}${tmpFsBtn}</div></div>`;
	}

	_bodyHTML() { return (this._viewMode === 'charts') ? this._chartsHTML() : this._resultsHTML(); }

	_filterBarHTML()
	{
		const f = this._filter; const P = this.providerName;
		const tmpAuthorChips = f.Authors.map((id) => `<span class="dol-tag">${this._esc(this._authorNames[id] || ('#' + id))}<button class="dol-tag-x" onclick="_Pict.providers.${P}.removeAuthor(${Number(id)})">&times;</button></span>`).join('');
		return /*html*/`
			<div class="dol-filters">
				<div class="dol-row">
					<span class="dol-search"><i class="fas fa-search dol-search-ic"></i><input class="dol-search-in" type="search" placeholder="Search documents…" value="${this._esc(f.SearchText)}" oninput="_Pict.providers.${P}.setSearch(this.value)" onkeydown="if(event.key==='Enter'){_Pict.providers.${P}.applyFilters();}" /></span>
					<div class="dol-f"><span class="dol-f-l">Type</span>${this._typeDropdownHTML()}</div>
					<div class="dol-f"><span class="dol-f-l">Status</span>${this._statusDropdownHTML()}</div>
					${this._layout.StateOptions ? `<div class="dol-f"><span class="dol-f-l">State</span>${this._stateDropdownHTML()}</div>` : ''}
					<div class="dol-f"><span class="dol-f-l">Kind</span><div id="DocumentList-KindPicker" class="dol-picker dol-enumpicker"></div></div>
					<label class="dol-f"><span class="dol-f-l">From</span><input type="date" value="${this._esc(f.From)}" onchange="_Pict.providers.${P}.setDateFrom(this.value)" /></label>
					<label class="dol-f"><span class="dol-f-l">To</span><input type="date" value="${this._esc(f.To)}" onchange="_Pict.providers.${P}.setDateTo(this.value)" /></label>
					<button class="dol-apply" onclick="_Pict.providers.${P}.applyFilters()">Apply</button>
					<button class="dol-clear" onclick="_Pict.providers.${P}.clearFilters()">Clear</button>
				</div>
				<div class="dol-row dol-row-pickers">
					${this._embeddedProjectID ? '' : `<div class="dol-pick"><span class="dol-f-l">Project</span><div id="DocumentList-ProjectPicker" class="dol-picker"></div></div>`}
					<div class="dol-pick"><span class="dol-f-l">Author</span><div id="DocumentList-AuthorPicker" class="dol-picker"></div>${tmpAuthorChips ? `<div class="dol-tags">${tmpAuthorChips}</div>` : ''}</div>
				</div>
			</div>`;
	}

	_typeDropdownHTML()
	{
		const P = this.providerName; const opts = this._typeOptions || [];
		const sel = this._filter.Types.length;
		const btn = `<button type="button" class="dol-ddbtn${this._typeDropdownOpen ? ' dol-ddbtn-open' : ''}" onclick="_Pict.providers.${P}.toggleTypeDropdown()"><span class="dol-ddbtn-l">${sel ? `${sel} selected` : 'Any type'}</span>${sel ? `<span class="dol-ddbadge">${sel}</span>` : ''}<i class="fas fa-chevron-${this._typeDropdownOpen ? 'up' : 'down'} dol-ddcaret"></i></button>`;
		if (!this._typeDropdownOpen) { return `<div class="dol-typedd dol-dd-type">${btn}</div>`; }
		const items = opts.map((o) =>
		{
			const on = this._filter.Types.indexOf(o.Hash) >= 0;
			return `<label class="dol-dditem${on ? ' dol-dditem-on' : ''}"><input type="checkbox" ${on ? 'checked' : ''} onclick="_Pict.providers.${P}.toggleType('${this._esc(o.Hash)}')" /><span class="dol-ddname" title="${this._esc(o.Label)}">${this._esc(o.Label)}</span></label>`;
		}).join('');
		return `<div class="dol-typedd dol-dd-type">${btn}<div class="dol-ddpanel"><div class="dol-ddpanel-head"><span>${opts.length} type${opts.length === 1 ? '' : 's'}</span>${sel ? `<button type="button" class="dol-ddclearbtn" onclick="_Pict.providers.${P}.clearTypes()">Clear</button>` : ''}</div><div class="dol-ddlist">${items || '<div class="dol-ddempty">No types resolved</div>'}</div></div></div>`;
	}

	_statusDropdownHTML()
	{
		const P = this.providerName; const sel = this._filter.Statuses.length;
		const btn = `<button type="button" class="dol-ddbtn${this._statusDropdownOpen ? ' dol-ddbtn-open' : ''}" onclick="_Pict.providers.${P}.toggleStatusDropdown()"><span class="dol-ddbtn-l">${sel ? `${sel} selected` : 'Any status'}</span>${sel ? `<span class="dol-ddbadge">${sel}</span>` : ''}<i class="fas fa-chevron-${this._statusDropdownOpen ? 'up' : 'down'} dol-ddcaret"></i></button>`;
		if (!this._statusDropdownOpen) { return `<div class="dol-typedd dol-dd-status">${btn}</div>`; }
		const tmpOptions = this._layout.StatusOptions || _STATUSES.map((pStatus) => ({ id: pStatus, text: pStatus }));
		const items = tmpOptions.map((pOption) =>
		{
			const on = this._filter.Statuses.indexOf(pOption.id) >= 0;
			const tmpInput = `<input type="checkbox" ${on ? 'checked' : ''} onclick="_Pict.providers.${P}.toggleStatus(${this._esc(JSON.stringify(pOption.id))})" />`;
			return `<label class="dol-dditem${on ? ' dol-dditem-on' : ''}">${tmpInput}<span class="dol-statusdot" style="background:${_STATUS_COLOR[pOption.id] || '#8b98a3'}"></span><span class="dol-ddname">${this._esc(pOption.text)}</span></label>`;
		}).join('');
		return `<div class="dol-typedd dol-dd-status">${btn}<div class="dol-ddpanel"><div class="dol-ddlist">${items}</div></div></div>`;
	}

	/** @return {string} the State pick list (only when the customer's Document Filter manifest lists states) */
	_stateDropdownHTML()
	{
		const P = this.providerName;
		const tmpSelected = this._filter.States.length;
		const tmpLabel = `<span class="dol-ddbtn-l">${tmpSelected ? `${tmpSelected} selected` : 'Any state'}</span>${tmpSelected ? `<span class="dol-ddbadge">${tmpSelected}</span>` : ''}`;
		const tmpButton = `<button type="button" class="dol-ddbtn${this._stateDropdownOpen ? ' dol-ddbtn-open' : ''}" onclick="_Pict.providers.${P}.toggleStateDropdown()">${tmpLabel}<i class="fas fa-chevron-${this._stateDropdownOpen ? 'up' : 'down'} dol-ddcaret"></i></button>`;
		if (!this._stateDropdownOpen)
		{
			return `<div class="dol-typedd dol-dd-state">${tmpButton}</div>`;
		}
		const tmpItems = (this._layout.StateOptions || []).map((pOption) =>
		{
			const tmpOn = this._filter.States.indexOf(pOption.id) >= 0;
			return `<label class="dol-dditem${tmpOn ? ' dol-dditem-on' : ''}"><input type="checkbox" ${tmpOn ? 'checked' : ''} onclick="_Pict.providers.${P}.toggleState(${this._esc(JSON.stringify(pOption.id))})" /><span class="dol-ddname">${this._esc(pOption.text)}</span></label>`;
		}).join('');
		return `<div class="dol-typedd dol-dd-state">${tmpButton}<div class="dol-ddpanel"><div class="dol-ddlist">${tmpItems}</div></div></div>`;
	}

	_resultsHTML()
	{
		if (this._loading && !this._rows.length) { return `<div class="dol-empty"><span class="dol-spin"></span> Searching…</div>`; }
		if (this._error) { return `<div class="dol-empty dol-error">${this._esc(this._error)}</div>`; }
		if (!this._rows.length) { return `<div class="dol-empty">No documents match these filters.</div>`; }
		const tmpRows = this._rows.map((d) => this._rowHTML(d)).join('');
		return `<div class="dol-listwrap"><table class="dol-list"><thead><tr><th></th>${this._columns().map((pColumn) => `<th>${this._esc(pColumn.Label)}</th>`).join('')}<th></th></tr></thead><tbody>${tmpRows}</tbody></table></div>${this._paginationHTML()}`;
	}

	_rowHTML(pDoc)
	{
		const id = pDoc.IDDocument;
		const tmpType = this._typeLabel(pDoc.DocumentType);
		const tmpRawName = String(pDoc.Name == null ? '' : pDoc.Name).trim();
		// An untitled document often carries its type name as Name (e.g. "LADOTD (HDR)"), which would just duplicate the
		// Type column. Show a muted "Untitled" instead (the SequenceNumber sub-line below still identifies the row) rather
		// than echoing the Type or exposing the raw IDDocument. Form instances keep their meaningful "Form #<sequence>" label.
		const tmpHasDistinctName = tmpRawName && (tmpRawName.toLowerCase() !== String(tmpType || '').trim().toLowerCase());
		const tmpName = tmpHasDistinctName ? tmpRawName : (pDoc.Form ? `Form #${pDoc.SequenceNumber || id}` : '');
		const tmpAuthor = this._authorNames[pDoc.CreatingIDUser] || (pDoc.CreatingIDUser ? `#${pDoc.CreatingIDUser}` : '');
		const tmpStatus = pDoc.Status || 'Draft';
		const tmpColor = _STATUS_COLOR[tmpStatus] || '#8b98a3';
		const tmpKindIcon = pDoc.Form ? 'fa-file-pen' : 'fa-file-lines';
		const P = this.providerName;
		const tmpLocked = !!pDoc.Locked;
		const tmpLock = tmpLocked ? ` <i class="fas fa-lock dol-lock" title="Locked (read-only)"></i>` : '';
		// Clicking the row (or Edit) loads the document as HTML via the render service — editable when not Locked.
		return `<tr class="dol-r" onclick="_Pict.providers.${P}.openHTML(${id}, false)">
			<td class="dol-icon-cell"><span class="dol-kicon"><i class="fas ${tmpKindIcon}"></i></span></td>
			${this._cellsHTML(pDoc, { Type: tmpType, Name: tmpName, Author: tmpAuthor, Status: tmpStatus, Color: tmpColor, Lock: tmpLock })}
			<td class="dol-actions dol-nowrap" onclick="event.stopPropagation()">
				${tmpLocked ? '' : `<button type="button" class="dol-act dol-act-primary" title="Open in HTML (editable — not locked)" onclick="_Pict.providers.${P}.openHTML(${id}, false)"><i class="fas fa-pen-to-square"></i> Edit</button>`}
				<button type="button" class="dol-act" title="View read-only in HTML" onclick="_Pict.providers.${P}.openHTML(${id}, true)"><i class="fas fa-eye"></i> View</button>
				<button type="button" class="dol-act dol-act-icon" title="Download PDF" onclick="_Pict.providers.${P}.downloadPDF(${id})"><i class="fas fa-file-pdf"></i></button>
				${(!tmpLocked && this.canDeleteDocument()) ? `<button type="button" class="dol-act dol-act-icon dol-act-danger" title="Delete" onclick="_Pict.providers.${P}.deleteDocument(${id})"><i class="fas fa-trash"></i></button>` : ''}
			</td>
		</tr>`;
	}

	/**
	 * One row's cells for the current columns. The lock icon follows the first status column, else the name.
	 * @param {Record<string, any>} pDoc
	 * @param {{ Type: string, Name: string, Author: string, Status: string, Color: string, Lock: string }} pValues
	 * @return {string}
	 */
	_cellsHTML(pDoc, pValues)
	{
		const tmpColumns = this._columns();
		const tmpStatusColumn = tmpColumns.find((pColumn) => (pColumn.Key === 'StateName') || (pColumn.Key === 'Status'));
		const tmpLockKey = tmpStatusColumn ? tmpStatusColumn.Key : 'Name';
		const fBadge = (pText, pColor) => `<span class="dol-badge" style="color:${pColor};background:${pColor}1a;border-color:${pColor}55">${this._esc(pText)}</span>`;
		return tmpColumns.map((pColumn) =>
		{
			const tmpLock = (pColumn.Key === tmpLockKey) ? pValues.Lock : '';
			switch (pColumn.Key)
			{
			case 'DocumentType':
				return `<td><span class="dol-type">${this._esc(pValues.Type)}</span></td>`;
			case 'Name':
				return `<td class="dol-namecell"><span class="dol-name${pValues.Name ? '' : ' dol-name-untitled'}">${pValues.Name ? this._esc(pValues.Name) : 'Untitled'}</span>${pDoc.SequenceNumber ? `<span class="dol-seq">#${this._esc(pDoc.SequenceNumber)}</span>` : ''}${tmpLock}</td>`;
			case 'DocumentDate':
				return `<td class="dol-nowrap">${this._esc(this._fmtDate(pDoc.DocumentDate))}</td>`;
			case 'CreatingIDUser':
				return `<td>${this._esc(pValues.Author)}</td>`;
			case 'Status':
				return `<td>${fBadge(pValues.Status, pValues.Color)}${tmpLock}</td>`;
			case 'StateName':
				return `<td>${fBadge(pDoc.StateName || pValues.Status, pValues.Color)}${tmpLock}</td>`;
			case 'IDProject':
				return `<td>${this._esc(this._projectNames[pDoc.IDProject] || (pDoc.IDProject ? `#${pDoc.IDProject}` : ''))}</td>`;
			default:
				return '<td></td>';
			}
		}).join('');
	}

	_paginationHTML()
	{
		const tmpPages = Math.max(1, Math.ceil(this._total / this._cap));
		if (tmpPages <= 1) { return ''; }
		const P = this.providerName;
		const tmpStart = this._page * this._cap + 1; const tmpEnd = Math.min(this._total, (this._page + 1) * this._cap);
		return `<div class="dol-pager">
			<button class="dol-pg" ${this._page <= 0 ? 'disabled' : ''} onclick="_Pict.providers.${P}.setPage(${this._page - 1})"><i class="fas fa-chevron-left"></i> Prev</button>
			<span class="dol-pg-info">${tmpStart.toLocaleString()}–${tmpEnd.toLocaleString()} of ${this._total.toLocaleString()}</span>
			<button class="dol-pg" ${this._page >= (tmpPages - 1) ? 'disabled' : ''} onclick="_Pict.providers.${P}.setPage(${this._page + 1})">Next <i class="fas fa-chevron-right"></i></button>
		</div>`;
	}

	// --- charts (KPIs + approval-status funnel + type distribution) ---
	_chartsHTML()
	{
		if (this._error) { return `<div class="dol-empty dol-error">${this._esc(this._error)}</div>`; }
		if (this._loading && !this._chartsLoaded) { return `<div class="dol-empty"><span class="dol-spin"></span> Counting…</div>`; }
		const approved = this._chartStatus.Approved || 0;
		const pending = (this._chartStatus.Submitted || 0) + (this._chartStatus.Pending || 0);
		const approvedPct = this._total ? Math.round((approved / this._total) * 100) : 0;
		return /*html*/`
			<div class="dxp">
				<div class="dxp-badge-row"><span class="dxp-badge"><span class="dxp-pulse${this._loading ? ' dxp-pulse-busy' : ''}"></span> counted in <b id="dxp-badge-ms">${this._loading ? '…' : this._chartLatencyMs}</b> ms · Meadow (no index)</span></div>
				<div class="dxp-kpis">
					${this._kpi('Metric', 'total', this._total, 'Documents in view')}
					${this._kpi('Metric', 'types', this._chartTypes.filter((t) => t.count > 0).length, 'Document types')}
					${this._kpi('Metric', 'approved', approvedPct, 'Approved', '', '%')}
					${this._kpi('Attention', 'pending', pending, 'Awaiting approval', ' dxp-kpi-warn')}
				</div>
				<div class="dxp-grid">
					${this._statusChartHTML()}
					${this._typeChartHTML()}
				</div>
				<div class="dxp-foot"><i class="fas fa-bolt"></i> Documents have no search index, so each bar is a live <b>Count/FilteredTo</b> query (parallel) — clicking one sets the same filter the List shares. Approval actions (approve/reject/submit) are the next step.</div>
			</div>`;
	}
	_kpi(pEyebrow, pId, pVal, pLabel, pCls, pSuffix)
	{
		const prev = this._prevKPI ? (this._prevKPI[pId] != null ? this._prevKPI[pId] : pVal) : 0;
		return `<div class="dxp-kpi${pCls || ''}"><div class="dxp-eyebrow">${pEyebrow}</div><div class="dxp-v" data-kpi="${pId}" data-to="${pVal}" data-suffix="${pSuffix || ''}">${this._fmt(prev)}${pSuffix || ''}</div><div class="dxp-l">${pLabel}</div></div>`;
	}
	_statusChartHTML()
	{
		const P = this.providerName;
		const segs = _STATUSES.map((s) => ({ s, count: this._chartStatus[s] || 0, color: _STATUS_COLOR[s] }));
		const sum = segs.reduce((a, x) => a + x.count, 0) || 1;
		const sel = this._filter.Statuses;
		const stack = segs.map((x) => `<div class="dxp-seg${sel.length && sel.indexOf(x.s) < 0 ? ' dxp-seg-dim' : ''}" style="width:${(x.count / sum) * 100}%;background:${x.color}" title="${x.s} · ${this._fmt(x.count)}" onclick="_Pict.providers.${P}.toggleStatus('${x.s}')"></div>`).join('');
		const legend = segs.map((x) => `<div class="dxp-fl${sel.indexOf(x.s) >= 0 ? ' dxp-fl-on' : ''}" onclick="_Pict.providers.${P}.toggleStatus('${x.s}')"><span class="dxp-sw" style="background:${x.color}"></span><span class="dxp-fname">${x.s}</span><span class="dxp-fpct">${((x.count / sum) * 100).toFixed(1)}%</span><span class="dxp-fcnt">${this._fmt(x.count)}</span></div>`).join('');
		return `<div class="dxp-panel"><div class="dxp-ph"><h3><i class="fas fa-clipboard-check"></i> Approval status</h3><span class="dxp-hint">Document.Status</span></div><div class="dxp-stack">${stack}</div><div class="dxp-flegend">${legend}</div></div>`;
	}
	_typeChartHTML() { return `<div class="dxp-panel" id="dol-typepanel">${this._typeChartInnerHTML()}</div>`; }
	_typeChartInnerHTML()
	{
		const P = this.providerName; const rows = this._chartTypes;
		const prog = this._typeProgress || { done: 0, total: 0 };
		const loading = (prog.total > 0) && (prog.done < prog.total);
		const max = rows.reduce((a, r) => Math.max(a, r.count), 1);
		const body = rows.map((r, i) =>
		{
			const on = this._filter.Types.indexOf(r.hash) >= 0;
			const w = Math.max(1.5, (r.count / max) * 100); const color = _CAT[i % _CAT.length];
			return `<div class="dxp-row${on ? ' dxp-row-on' : ''}" onclick="_Pict.providers.${P}.toggleType('${this._esc(r.hash)}')">
				<div class="dxp-row-bg" style="width:${w}%;background:linear-gradient(90deg,${color}22,transparent)"></div>
				<div class="dxp-lab"><span class="dxp-dot" style="background:${color}"></span><span class="dxp-name" title="${this._esc(r.label)}">${this._esc(r.label)}</span></div>
				<span class="dxp-cnt">${this._fmt(r.count)}</span>
				<div class="dxp-track"><div class="dxp-fill" style="width:${w}%;background:${color}"></div></div>
			</div>`;
		}).join('');
		const hint = loading
			? `<span class="dxp-hint"><span class="dol-spin"></span> counting types… ${prog.done}/${prog.total}</span>`
			: `<span class="dxp-hint">DocumentType — click to filter</span>`;
		return `<div class="dxp-ph"><h3><i class="fas fa-file-lines"></i> Document type</h3>${hint}</div><div class="dxp-rows">${body || (loading ? '' : '<div class="dol-empty">No types in view.</div>')}</div>`;
	}
	_tweenChartKPIs()
	{
		const tmpCA = this.pict.ContentAssignment; if (!tmpCA) { return; }
		const els = tmpCA.getElement(`${this._mount} .dxp-v`);
		const reduced = (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches);
		const next = {};
		for (let i = 0; els && i < els.length; i++)
		{
			const el = els[i]; const key = el.getAttribute('data-kpi'); const to = Number(el.getAttribute('data-to')) || 0; const sfx = el.getAttribute('data-suffix') || '';
			const from = this._prevKPI && this._prevKPI[key] != null ? this._prevKPI[key] : 0; next[key] = to;
			if (reduced || from === to) { el.textContent = this._fmt(to) + sfx; continue; }
			this._animateKPI(el, from, to, sfx);
		}
		this._prevKPI = next;
	}
	_animateKPI(el, from, to, sfx)
	{
		const dur = 460; let t0 = null;
		const step = (ts) => { if (t0 === null) { t0 = ts; } const p = Math.min(1, (ts - t0) / dur); const e = 1 - Math.pow(1 - p, 3); el.textContent = this._fmt(from + (to - from) * e) + sfx; if (p < 1) { requestAnimationFrame(step); } };
		requestAnimationFrame(step);
	}

	// --- pickers (project + author, searchable, via pict-section-picker) ---
	_mountPickers()
	{
		const tmpPicker = this.pict.providers && this.pict.providers['Pict-Section-Picker'];
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		if (tmpCA.getElement('#DocumentList-ProjectPicker').length > 0)
		{
			const v = tmpPicker.createPicker(`${this.providerName}-ProjectPicker`, {
				DestinationAddress: '#DocumentList-ProjectPicker', ValueAddress: `AppData.${this.providerName}.PickerProject`,
				Searchable: true, AllowClear: true, ClearLabel: '— any project —', Placeholder: 'Any project…',
				DataProvider: this._projectDataProvider(), ResolveValue: this._projectResolveValue(),
				OnChange: (val, pRecord) => this.selectProject(val, pRecord && (pRecord.Text || pRecord.Name)),
			});
			if (v && (typeof v.render === 'function')) { v.render(); }
			if (v && (typeof v.setValue === 'function')) { v.setValue(this._filter.IDProject || ''); }
		}
		if (tmpCA.getElement('#DocumentList-AuthorPicker').length > 0)
		{
			const v = tmpPicker.createPicker(`${this.providerName}-AuthorPicker`, {
				DestinationAddress: '#DocumentList-AuthorPicker', ValueAddress: `AppData.${this.providerName}.PickerAuthor`,
				Searchable: true, AllowClear: true, ClearLabel: '— add author —', Placeholder: 'Add an author…',
				DataProvider: this._authorDataProvider(), ResolveValue: () => Promise.resolve(null),
				OnChange: (val, pRecord) => { if (val) { this.addAuthor(val, pRecord && (pRecord.Text || pRecord.Name)); } },
			});
			if (v && (typeof v.render === 'function')) { v.render(); }
		}
		if (tmpCA.getElement('#DocumentList-KindPicker').length > 0)
		{
			const v = tmpPicker.createPicker(`${this.providerName}-KindPicker`, {
				DestinationAddress: '#DocumentList-KindPicker', ValueAddress: `AppData.${this.providerName}.PickerKind`,
				Searchable: true, AllowClear: true, ClearLabel: 'All', Placeholder: 'All',
				DataProvider: this._kindDataProvider(), ResolveValue: (val) => Promise.resolve(this._kindOption(val)),
				OnChange: (val) => this.setForm(val == null ? '' : String(val)),
			});
			if (v && (typeof v.render === 'function')) { v.render(); }
			if (v && (typeof v.setValue === 'function')) { v.setValue(this._filter.Form || ''); }
		}
	}
	_projectDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'Projects/Lite', IDField: 'IDProject',
				ToOption: (r) => ({ Value: Number(r.IDProject), Text: r.Value || r.Name || `Project ${r.IDProject}`, Record: r }),
			});
	}
	_projectResolveValue() { return (v) => { const id = Number(v); if (!id) { return Promise.resolve(null); } return this._get(`Project/${id}`).then((r) => ((r && r.IDProject) ? { Value: id, Text: r.Name || `#${id}` } : { Value: id, Text: `#${id}` })).catch(() => ({ Value: id, Text: `#${id}` })); }; }
	_authorDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._get(pURL), { Scope: 'FBV~Deleted~EQ~0', PageSize: 25 });
	}
	_KIND_OPTIONS() { return [ { Value: '0', Text: 'Reports' }, { Value: '1', Text: 'Forms' } ]; }
	_kindOption(pValue) { const v = String(pValue == null ? '' : pValue); return this._KIND_OPTIONS().find((o) => o.Value === v) || null; }
	_kindDataProvider()
	{
		return (pTerm) =>
		{
			const t = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
			const rows = this._KIND_OPTIONS().filter((o) => !t || o.Text.toLowerCase().indexOf(t) >= 0);
			return Promise.resolve({ results: rows, hasMore: false });
		};
	}
}

module.exports = DocumentListProvider;
module.exports.default_configuration = { ProviderIdentifier: 'DocumentList' };

const _CSS = /*css*/`
	/* Consistent horizontal gutter for the whole dashboard chrome (this screen runs full-bleed in Dashboard Mode, so the
	   page has no side padding). Standalone only — embedded in a Project tab the tab host supplies its own padding. The wide
	   results table full-bleeds back out to the edge (below) so it still uses the whole browser width. */
	.dol { width: 100%; --dol-gutter: clamp(1rem, 2.2vw, 2rem); }
	.dol:not(.dol-embedded) { padding-left: var(--dol-gutter); padding-right: var(--dol-gutter); }
	.dol.dol-embedded { --dol-gutter: 0px; }
	.dol-head { margin: 0 0 0.8rem; } .dol-title { font-size: 1.1rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); display: inline-flex; align-items: center; gap: 0.5rem; } .dol-title i { color: var(--theme-color-brand-primary,#2e7d74); }
	.dol-filters { background: var(--theme-color-background-secondary,#f4f6f9); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 10px; padding: 0.75rem 0.85rem; margin: 0 0 1rem; }
	.dol-row { display: flex; align-items: flex-end; gap: 0.6rem 0.8rem; flex-wrap: wrap; } .dol-row-pickers { margin-top: 0.6rem; }
	.dol-search { position: relative; flex: 1 1 16rem; min-width: 12rem; }
	.dol-search-ic { position: absolute; left: 0.6rem; top: 50%; transform: translateY(-50%); color: var(--theme-color-text-muted,#9aa3af); font-size: 0.8rem; }
	.dol-search-in { width: 100%; font-size: 0.88rem; padding: 0.42rem 0.6rem 0.42rem 1.9rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); }
	.dol-f { display: flex; flex-direction: column; gap: 0.15rem; font-size: 0.82rem; }
	.dol-f-l { font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary,#45505f); }
	.dol-f input, .dol-f select { font-size: 0.85rem; padding: 0.38rem 0.5rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); }
	.dol-apply { font-size: 0.84rem; font-weight: 600; color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border: none; border-radius: 7px; padding: 0.42rem 0.85rem; cursor: pointer; }
	.dol-clear { font-size: 0.84rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: transparent; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; padding: 0.42rem 0.75rem; cursor: pointer; }
	.dol-pick { display: flex; flex-direction: column; gap: 0.15rem; flex: 1 1 16rem; min-width: 13rem; } .dol-picker { min-width: 12rem; }
	.dol-tags { display: flex; gap: 0.3rem; flex-wrap: wrap; margin-top: 0.3rem; }
	.dol-tag { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.76rem; font-weight: 600; color: var(--theme-color-text-primary,#1f2733); background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 999px; padding: 0.08rem 0.2rem 0.08rem 0.5rem; }
	.dol-tag-x { border: none; background: transparent; color: var(--theme-color-text-muted,#98a2b3); cursor: pointer; font-size: 0.95rem; line-height: 1; }

	.dol-typedd { position: relative; display: inline-block; }
	.dol-ddbtn { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; padding: 0.38rem 0.6rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); color: var(--theme-color-text-primary,#1f2733); cursor: pointer; white-space: nowrap; min-width: 8rem; justify-content: space-between; }
	.dol-ddbtn:hover, .dol-ddbtn-open { border-color: var(--theme-color-brand-primary,#2e7d74); }
	.dol-ddbtn-open { box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 18%, transparent); }
	.dol-ddcaret { font-size: 0.7rem; color: var(--theme-color-text-muted,#8894a4); }
	.dol-ddbadge { background: var(--theme-color-brand-primary,#2e7d74); color: #fff; font-size: 0.66rem; font-weight: 700; border-radius: 999px; padding: 0.02rem 0.4rem; }
	.dol-ddpanel { position: absolute; top: calc(100% + 0.35rem); left: 0; z-index: 40; width: 17rem; max-width: 82vw; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 9px; box-shadow: 0 12px 30px -12px rgba(20,40,50,0.3); overflow: hidden; }
	.dol-ddpanel-head { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light,#eef2f4); font-size: 0.66rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#8b98a3); }
	.dol-ddclearbtn { border: none; background: transparent; color: var(--theme-color-brand-primary,#2e7d74); font: inherit; font-size: 0.7rem; font-weight: 700; cursor: pointer; }
	.dol-ddlist { max-height: 16rem; overflow-y: auto; padding: 0.3rem; }
	.dol-dditem { display: flex; align-items: center; gap: 0.55rem; padding: 0.35rem 0.5rem; border-radius: 6px; cursor: pointer; font-size: 0.85rem; }
	.dol-dditem:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.dol-dditem input { accent-color: var(--theme-color-brand-primary,#2e7d74); width: 0.95rem; height: 0.95rem; cursor: pointer; flex: none; }
	.dol-ddname { flex: 1; color: var(--theme-color-text-primary,#1f2733); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.dol-dditem-on .dol-ddname { font-weight: 600; }
	.dol-statusdot { width: 0.6rem; height: 0.6rem; border-radius: 50%; flex: none; }
	.dol-ddempty { padding: 0.6rem; color: var(--theme-color-text-muted,#9aa3af); font-size: 0.82rem; text-align: center; }

	.dol-viewtabs-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin: 0 0 1rem; flex-wrap: wrap; }
	.dol-viewtabs { display: inline-flex; gap: 0.15rem; padding: 0.2rem; background: var(--theme-color-background-secondary,#eef1f6); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 10px; }
	.dol-vtab { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-secondary,#54636e); background: transparent; border: none; border-radius: 7px; padding: 0.38rem 0.85rem; cursor: pointer; }
	.dol-vtab-on { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); }
	.dol-vtabcount { font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-muted,#6b7686); font-variant-numeric: tabular-nums; }
	.dol-vtabs-right { display: flex; align-items: center; gap: 0.55rem 0.9rem; flex-wrap: wrap; }
	.dol-embedded .dol-filters { margin-top: 0; }

	/* --- full-screen toggle + overlay --- */
	.dol-fsbtn { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 8px; padding: 0.36rem 0.7rem; cursor: pointer; white-space: nowrap; }
	.dol-fsbtn:hover { border-color: var(--theme-color-brand-primary,#2e7d74); color: var(--theme-color-brand-primary,#2e7d74); }
	.dol-fsbtn i { font-size: 0.78rem; opacity: 0.85; }
	.dol-fsbtn-on { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-color: var(--theme-color-brand-primary,#2e7d74); }
	.dol-fsbtn-on:hover { color: #fff; filter: brightness(1.05); }
	.dol-fsbtn-on i { opacity: 1; }
	/* Overlay fills the whole browser view, above the app nav (top navbar z-index 30, bottom bar 1050). 'transform' composites
	   the layer from creation so it paints immediately (avoids the "position:fixed blank until scroll" quirk). */
	.dol.dol-fs { position: fixed; inset: 0; z-index: 1100; overflow: auto; background: var(--theme-color-background-tertiary,#f3f4f6); padding-top: 1.1rem; padding-bottom: 1.6rem; transform: translateZ(0); }

	.dol-listwrap { width: auto; overflow-x: auto; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 10px; }
	/* Wide tabular data uses the WHOLE browser width + scrolls: cancel the chrome gutter so the table band reaches the edges. */
	.dol:not(.dol-embedded) .dol-listwrap { margin-left: calc(-1 * var(--dol-gutter)); margin-right: calc(-1 * var(--dol-gutter)); border-left: none; border-right: none; border-radius: 0; }
	.dol-list { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
	.dol-list th { text-align: left; font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#6b7686); background: var(--theme-color-background-tertiary,#f4f6f9); padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light,#e8ebf0); white-space: nowrap; }
	.dol-list td { padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light,#f1f4f8); vertical-align: middle; }
	.dol-r { cursor: pointer; } .dol-r:hover td { background: var(--theme-color-background-secondary,#f6f8fb); }
	.dol-icon-cell { width: 2.4rem; } .dol-kicon { display: inline-flex; align-items: center; justify-content: center; width: 1.9rem; height: 1.9rem; border-radius: 6px; background: var(--theme-color-background-secondary,#eef1f6); color: var(--theme-color-brand-primary,#2e7d74); }
	.dol-type { font-size: 0.72rem; font-weight: 700; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border-radius: 5px; padding: 0.08rem 0.4rem; white-space: nowrap; }
	.dol-namecell { min-width: 16rem; } .dol-name { display: block; color: var(--theme-color-text-primary,#1f2733); } .dol-name-untitled { font-style: italic; color: var(--theme-color-text-muted,#9aa3af); } .dol-seq { display: block; font-size: 0.72rem; color: var(--theme-color-text-muted,#9aa3af); }
	.dol-nowrap { white-space: nowrap; }
	.dol-badge { font-size: 0.72rem; font-weight: 700; border: 1px solid; border-radius: 999px; padding: 0.1rem 0.55rem; white-space: nowrap; }
	.dol-actions { text-align: right; } .dol-actions .dol-act { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.76rem; font-weight: 600; border: 1px solid var(--theme-color-border-medium,#d5dbe3); background: var(--theme-color-background-primary,#fff); color: var(--theme-color-text-secondary,#45505f); border-radius: 6px; padding: 0.25rem 0.55rem; cursor: pointer; margin-left: 0.3rem; }
	.dol-act:hover { border-color: var(--theme-color-brand-primary,#2e7d74); color: var(--theme-color-brand-primary,#2e7d74); }
	.dol-act-danger:hover { border-color: var(--theme-color-danger,#c0392b); color: var(--theme-color-danger,#c0392b); }
	.dol-act-primary { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-color: var(--theme-color-brand-primary,#2e7d74); } .dol-act-primary:hover { color: #fff; filter: brightness(1.05); }
	.dol-act-icon { padding: 0.25rem 0.45rem; } .dol-lock { color: var(--theme-color-text-muted,#9aa3af); font-size: 0.72rem; margin-left: 0.35rem; }
	.dol-empty { padding: 2.4rem 1rem; text-align: center; color: var(--theme-color-text-muted,#8a94a3); font-size: 0.9rem; } .dol-empty.dol-error { color: #c0392b; }
	.dol-spin { display: inline-block; width: 1rem; height: 1rem; border: 2px solid var(--theme-color-border-medium,#d5dbe3); border-top-color: var(--theme-color-brand-primary,#2e7d74); border-radius: 50%; animation: dol-sp 0.7s linear infinite; vertical-align: -2px; }
	@keyframes dol-sp { to { transform: rotate(360deg); } }
	.dol-pager { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 0.9rem; }
	.dol-pg { font-size: 0.84rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; padding: 0.35rem 0.7rem; cursor: pointer; } .dol-pg[disabled] { opacity: 0.45; cursor: default; }
	.dol-pg-info { font-size: 0.82rem; color: var(--theme-color-text-muted,#6b7686); font-variant-numeric: tabular-nums; }

	/* charts */
	.dxp { width: 100%; } .dxp-badge-row { display: flex; justify-content: flex-end; margin: 0 0 0.7rem; }
	.dxp-badge { display: inline-flex; align-items: center; gap: 0.5rem; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 999px; padding: 0.35rem 0.8rem; font-size: 0.76rem; color: var(--theme-color-text-muted,#6b7686); }
	.dxp-badge b { color: var(--theme-color-brand-primary,#2e7d74); font-variant-numeric: tabular-nums; }
	.dxp-pulse { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: #2f8f5b; } .dxp-pulse-busy { background: var(--theme-color-brand-primary,#2e7d74); animation: dol-blink 0.7s linear infinite; } @keyframes dol-blink { 50% { opacity: 0.25; } }
	.dxp-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.85rem; margin: 0 0 0.9rem; }
	.dxp-kpi { background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 13px; padding: 0.9rem 1rem; position: relative; overflow: hidden; }
	.dxp-eyebrow { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--theme-color-text-muted,#8b98a3); }
	.dxp-v { font-size: 1.85rem; font-weight: 800; line-height: 1; margin: 0.5rem 0 0.3rem; letter-spacing: -0.02em; color: var(--theme-color-text-primary,#1f2733); font-variant-numeric: tabular-nums; }
	.dxp-l { font-size: 0.76rem; color: var(--theme-color-text-muted,#6b7686); }
	.dxp-kpi-warn .dxp-v { color: #bd6631; } .dxp-kpi-warn::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: #bd6631; }
	.dxp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; align-items: start; }
	.dxp-panel { background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 13px; padding: 0.9rem 1rem 1rem; min-width: 0; }
	.dxp-ph { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; margin: 0 0 0.8rem; }
	.dxp-ph h3 { font-size: 0.92rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); display: flex; align-items: center; gap: 0.45rem; margin: 0; } .dxp-ph h3 i { color: var(--theme-color-brand-primary,#2e7d74); font-size: 0.85rem; }
	.dxp-hint { font-size: 0.68rem; color: var(--theme-color-text-muted,#9aa3af); }
	.dxp-stack { display: flex; height: 2.4rem; border-radius: 9px; overflow: hidden; border: 1px solid var(--theme-color-border-light,#e2e8ec); margin: 0 0 0.7rem; }
	.dxp-seg { height: 100%; cursor: pointer; transition: width 0.5s cubic-bezier(.22,1,.36,1), opacity 0.15s; min-width: 2px; } .dxp-seg:hover { filter: brightness(1.07); } .dxp-seg-dim { opacity: 0.3; }
	.dxp-flegend { display: flex; flex-direction: column; gap: 0.25rem; }
	.dxp-fl { display: grid; grid-template-columns: auto 1fr auto auto; align-items: center; gap: 0.6rem; padding: 0.3rem 0.5rem; border-radius: 8px; cursor: pointer; font-size: 0.82rem; border: 1px solid transparent; } .dxp-fl:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.dxp-fl-on { background: var(--theme-color-brand-primary-subtle,#e3f1ee); border-color: color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 30%, transparent); }
	.dxp-sw { width: 0.7rem; height: 0.7rem; border-radius: 50%; } .dxp-fname { color: var(--theme-color-text-primary,#1f2733); } .dxp-fpct { color: var(--theme-color-text-muted,#9aa3af); font-variant-numeric: tabular-nums; } .dxp-fcnt { font-weight: 600; font-variant-numeric: tabular-nums; color: var(--theme-color-text-secondary,#54636e); }
	.dxp-rows { display: flex; flex-direction: column; gap: 0.15rem; }
	.dxp-row { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 0.6rem; padding: 0.32rem 0.5rem; border-radius: 8px; cursor: pointer; position: relative; border: 1px solid transparent; } .dxp-row:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.dxp-row-on { background: var(--theme-color-brand-primary-subtle,#e3f1ee); border-color: color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 32%, transparent); }
	.dxp-row-bg { position: absolute; left: 0.5rem; right: 0.5rem; top: 0.2rem; bottom: 0.2rem; border-radius: 6px; z-index: 0; transition: width 0.5s cubic-bezier(.22,1,.36,1); }
	.dxp-lab { display: flex; align-items: center; gap: 0.55rem; min-width: 0; z-index: 1; } .dxp-dot { width: 0.6rem; height: 0.6rem; border-radius: 3px; flex: none; }
	.dxp-name { font-size: 0.82rem; color: var(--theme-color-text-primary,#1f2733); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; } .dxp-row-on .dxp-name { font-weight: 600; color: var(--theme-color-brand-primary,#2e7d74); }
	.dxp-cnt { font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-secondary,#54636e); font-variant-numeric: tabular-nums; z-index: 1; } .dxp-row-on .dxp-cnt { color: var(--theme-color-brand-primary,#2e7d74); }
	.dxp-track { position: absolute; left: 0.5rem; right: 0.5rem; bottom: 0.15rem; height: 2px; border-radius: 2px; background: var(--theme-color-border-light,#eef2f4); overflow: hidden; z-index: 0; } .dxp-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 2px; transition: width 0.5s cubic-bezier(.22,1,.36,1); }
	.dxp-foot { margin-top: 1rem; font-size: 0.72rem; color: var(--theme-color-text-muted,#9aa3af); line-height: 1.6; } .dxp-foot i { color: #2f8f5b; } .dxp-foot b { color: var(--theme-color-text-secondary,#54636e); }

	@media (max-width: 900px) { .dxp-kpis { grid-template-columns: repeat(2, 1fr); } .dxp-grid { grid-template-columns: 1fr; } }
	@media (prefers-reduced-motion: reduce) { .dxp-seg, .dxp-row-bg, .dxp-fill { transition: none; } .dxp-pulse-busy { animation: none; } }
`;
